import React, { useEffect, useRef, useState } from "react";
import BlueMascot from "./BlueMascot";
import BlueMessage from "./BlueMessage";
import BlueSuggestions from "./BlueSuggestions";

/**
 * BlueChatWindow — état "ouvert" du chat.
 *
 * Le composant ne connaît ni le fournisseur IA, ni le contexte de page :
 * il expose simplement un callback `onSend(text)` et affiche les messages
 * que lui passe le parent.
 */
export function BlueChatWindow({
  config,
  messages,
  state, // "idle" | "thinking"
  onSend,
  onClose,
}) {
  const [value, setValue] = useState("");
  const [discExpanded, setDiscExpanded] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Auto-scroll en bas à chaque nouveau message ou changement d'état.
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, state]);

  useEffect(() => {
    // Focus à l'ouverture pour l'accessibilité clavier.
    inputRef.current?.focus();
  }, []);

  const submit = (e) => {
    e?.preventDefault?.();
    const text = value.trim();
    if (!text || state === "thinking") return;
    setValue("");
    onSend(text);
  };

  return (
    <section
      className="blue-chat-window"
      role="dialog"
      aria-label={`Conversation avec ${config.name}`}
      data-testid="blue-chat-window"
    >
      <header className="blue-chat-window__header">
        <div className="blue-chat-window__identity">
          <div className="blue-chat-window__avatar">
            <BlueMascot state={state} variant="avatar" size={56} />
          </div>
          <div className="blue-chat-window__meta">
            <h2 className="blue-chat-window__name" data-testid="blue-name">
              {config.name}
            </h2>
            <p className="blue-chat-window__status" data-testid="blue-status">
              {state === "thinking" ? config.strings.thinkingLabel : config.strings.onlineLabel}
            </p>
          </div>
        </div>

        <button
          type="button"
          className="blue-chat-window__close"
          onClick={onClose}
          aria-label={config.strings.closeLabel}
          data-testid="blue-chat-close-button"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M6 6 L18 18 M18 6 L6 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        </button>
      </header>

      {/* Phase 14 STEP 5 — transparence IA : visible dès la 1re interaction,
          non bloquant, non répété sous chaque message, rouvrable/relisible. */}
      <div className="blue-disclosure" data-testid="blue-ai-disclosure">
        <button
          type="button"
          className="blue-disclosure__toggle"
          onClick={() => setDiscExpanded((v) => !v)}
          aria-expanded={discExpanded}
          data-testid="blue-ai-disclosure-toggle"
        >
          <span className="blue-disclosure__icon" aria-hidden="true">ⓘ</span>
          <span className="blue-disclosure__short">{config.strings.aiDisclosureShort}</span>
          <span className="blue-disclosure__more">
            {discExpanded ? config.strings.aiDisclosureLess : config.strings.aiDisclosureMore}
          </span>
        </button>
        {discExpanded && (
          <p className="blue-disclosure__full" data-testid="blue-ai-disclosure-full">
            {config.strings.aiDisclosureFull}
          </p>
        )}
      </div>

      <div
        className="blue-chat-window__history"
        ref={scrollRef}
        data-testid="blue-chat-history"
      >
        {messages.map((m, i) => (
          <BlueMessage
            key={m.id || i}
            role={m.role}
            content={m.content}
            sources={m.sources}
            testId={`blue-msg-${m.role}-${m.id || i}`}
          />
        ))}

        {/* Suggestions cliquables — visibles uniquement avant la 1re
            interaction utilisateur (seul le message d'accueil est présent). */}
        {messages.length <= 1 && config.suggestions?.length > 0 && (
          <BlueSuggestions
            suggestions={config.suggestions}
            label={config.strings.suggestionsLabel}
            onPick={(text) => onSend(text)}
            disabled={state === "thinking"}
          />
        )}

        {state === "thinking" && (
          <div
            className="blue-msg blue-msg--blue"
            data-testid="blue-thinking-indicator"
          >
            <div className="blue-msg__bubble blue-msg__bubble--thinking">
              <span className="blue-dot" />
              <span className="blue-dot" />
              <span className="blue-dot" />
            </div>
          </div>
        )}
      </div>

      <form className="blue-chat-window__composer" onSubmit={submit}>
        <label htmlFor="blue-chat-input" className="blue-chat-sr">
          {config.strings.inputPlaceholder}
        </label>
        <input
          id="blue-chat-input"
          ref={inputRef}
          className="blue-chat-window__input"
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={config.strings.inputPlaceholder}
          maxLength={config.inputMaxLength}
          disabled={state === "thinking"}
          data-testid="blue-chat-input"
        />
        <button
          type="submit"
          className="blue-chat-window__send"
          disabled={state === "thinking" || value.trim().length === 0}
          aria-label={config.strings.sendLabel}
          data-testid="blue-chat-send-button"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M4 12 L20 4 L14 20 L12 13 L4 12 Z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </button>
      </form>
    </section>
  );
}

export default BlueChatWindow;
